'use client';

import { Box, VStack } from '@terra/ui-styled-system';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { useTranslations } from 'next-intl';
import { ParallelAgentRow } from './ParallelAgentRow';
import {
  isParallelAgentAlive,
  parallelAgentStatus,
} from './parallel-agent-batch';
import type { ParallelAgentExecution } from './parallel-agent-execution';
import { useIndexedSubagentSources } from './useParallelAgentRowCopy';

export function ParallelAgentsList({
  members,
  messages,
  selectedToolCallId,
  onOpen,
  onStop,
  canStopExecution,
}: {
  members: ParallelAgentExecution[];
  messages: TerraUIMessage[];
  selectedToolCallId: string | undefined;
  onOpen: (toolCallId: string) => void;
  onStop?: (execution: ParallelAgentExecution) => void;
  canStopExecution?: (execution: ParallelAgentExecution) => boolean;
}) {
  const t = useTranslations('designSystem.chat.parallelAgents');
  const runningMembers = members.filter((execution) =>
    isParallelAgentAlive(parallelAgentStatus(execution)),
  );
  const sources = useIndexedSubagentSources({
    messages,
    parentToolCallIds: runningMembers.map((execution) => execution.toolCallId),
  });

  if (runningMembers.length === 0) {
    return null;
  }

  return (
    <Box
      width="full"
      maxH="min(50vh, 360px)"
      overflowY="auto"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="lg"
      bg="bg.default"
      boxShadow="md"
      px={1.5}
      py={1}
      animation="fade-in 0.15s ease-out"
      data-testid="parallel-agents-list"
    >
      <VStack
        alignItems="stretch"
        gap={0.5}
        width="full"
        maxH="160px"
        overflowY="auto"
      >
        {runningMembers.map((execution) => (
          <ParallelAgentRow
            key={execution.toolCallId}
            execution={execution}
            messages={messages}
            isSelected={execution.toolCallId === selectedToolCallId}
            stopLabel={t('stop')}
            task={sources.tasks.get(execution.toolCallId)}
            indexedActivity={sources.activities.get(execution.toolCallId)}
            onOpen={() => onOpen(execution.toolCallId)}
            onStop={
              !onStop ||
              execution.kind === 'neo' ||
              (canStopExecution && !canStopExecution(execution))
                ? undefined
                : () => onStop(execution)
            }
          />
        ))}
      </VStack>
    </Box>
  );
}
